<script lang="ts" module>
  import {
    createDateFormatter,
    isCheckoutPageShipping,
    formatEstimatedDeliveryDates,
    type CheckoutSdk,
  } from '@athena-lib/Checkout/utils';

  type SimpleShippingMethod = {
    title: string;
    formattedDate: string;
  };

  export const getSimpleShippingMethods = (
    checkoutState: CheckoutSdk['state'],
  ): SimpleShippingMethod[] => {
    const formatter = createDateFormatter(checkoutState);
    const { checkout } = checkoutState;

    if (!isCheckoutPageShipping(checkout?.shipping)) {
      return [];
    }

    return checkout.shipping.shippingMethods.map((shippingMethod) => {
      const formatted = formatEstimatedDeliveryDates(formatter, shippingMethod);
      return {
        title: shippingMethod.title,
        formattedDate: formatted?.formattedDeliveryDate ?? '',
      };
    });
  };
</script>

<script lang="ts">
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import StackDivider from '@athena-lib/Stack/StackDivider.svelte';
  import { HeadingSmall, Text } from '@athena-lib/Text';
  import type { Snippet } from 'svelte';

  type Props = {
    estimatedDelivery: Snippet;
    shippingMethods: SimpleShippingMethod[];
  };

  const { estimatedDelivery, shippingMethods }: Props = $props();

  const translations = getCommonTranslations();
</script>

<div class="px-5 pb-5 pt-14">
  <div class="flex flex-col items-center">
    <HeadingSmall size="small">
      {translations.deliveryDetailsTitle}
    </HeadingSmall>
    <Text element="span">
      {translations.deliveryDetailsDescription}
    </Text>
    <div class="my-6">
      {@render estimatedDelivery()}
    </div>
  </div>
  <StackDivider dividers="default" />
  <div class="my-4 flex flex-col gap-4">
    <HeadingSmall size="xxsmall">
      {translations.availableDeliveryMethods}
    </HeadingSmall>
    <Text element="p">
      {translations.reliableCarrier}
    </Text>
    {#each shippingMethods as shippingMethod}
      <div class="flex flex-col gap-1">
        <Text element="span">{shippingMethod.title}</Text>
        <Text element="span" size="xsmall" color="dark" class="capitalize">
          {translations.getItBy}
          <Text element="span" size="xsmall" color="dark" bold>{shippingMethod.formattedDate}</Text>
        </Text>
      </div>
    {/each}
  </div>
  <StackDivider dividers="default" />
</div>
